import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  AppStorageService,
  CaseSelectService,
  ColorsService,
  DataService,
  DefaultConfigGuestService,
  DefaultConfigService,
  DemonstrationOfApplicantRightService,
  DocumentSectionService,
  FormAlertService,
  FormlyMatTreeSelectTypeComponent,
  GkModulesModule,
  GkUserSettingsModule,
  LoginService,
  MapSettingsService,
  MapStateService,
  PlannedApplicationMessageService,
  PortalsPermissionsService,
  ProxyInterceptor,
  PzService,
  RecaptchaService,
  RequestModeService,
  ShippingService,
  SystemCitiesDataService,
  SystemConfigBlobGuestService,
  SystemConfigGuestService,
} from '@gk/gk-modules';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccessibleNavigationComponent } from './components/accessible-navigation/accessible-navigation.component';
import { AppVersionUpdateComponent } from './components/app-version-update/app-version-update.component';
import { FooterComponent } from './components/footer/footer.component';
import { NavbarProviderComponent } from './components/navbar-provider/navbar-provider.component';
import { NoAccessComponent } from './components/no-access/no-access.component';
import { PerunSessionGuard } from './guards/perun-session/perun-session.guard';
import { PortalConfigAccessGuard } from './guards/portal-config-access/portal-config-access.guard';
import { SignInConfigAccessGuard } from './guards/sign-in-config-acccess/sign-in-config-access.guard';
import { ApiBaseHrefInterceptor } from './http-interceptors/api-base-href.interceptor';
import { AccessToInformationOnEnvironmentAndItsProtectionModule } from './modules/access-to-information-on-environment-and-its-protection/access-to-information-on-environment-and-its-protection.module';
import {
  AddressNumberRequestPortalModule,
  createTranslateLoader,
} from './modules/address-number-request-portal/address-number-request-portal.module';
import { AdjacentPropertyNeedEnterDecisionModule } from './modules/adjacent-property-need-enter-decision/adjacent-property-need-enter-decision.module';
import { BuildingOrPartUseChangeModule } from './modules/building-or-part-use-change/building-or-part-use-change.module';
import { BuildingPermitModificationDecisionModule } from './modules/building-permit-modification-decision/building-permit-modification-decision.module';
import { BuildingPermitTransferPortalModule } from './modules/building-permit-transfer-portal/building-permit-transfer-portal.module';
import { BuildingPermitModule } from './modules/building-permit/building-permit.module';
import { ConstructionIntentionTypeDictionaryService } from './modules/building-permit/services/construction-intention-type-dictionary/construction-intention-type-dictionary.service';
import { CommunalPremisePurchasePortalModule } from './modules/communal-premise-purchase-portal/communal-premise-purchase-portal.module';
import { ConstructionPortalModule } from './modules/construction-portal/construction-portal.module';
import { ExclusionPurposeService } from './modules/construction-portal/services/exclusion-purpose/exclusion-purpose.service';
import { DemolitionPermitModule } from './modules/demolition-permit/demolition-permit.module';
import { DesignerPortalModule } from './modules/designer-portal/designer-portal.module';
import { EcoPortalModule } from './modules/eco-portal/eco-portal.module';
import { EnvironmentalProtectionPortalModule } from './modules/environmental-protection-portal/environmental-protection-portal.module';
import { ExcludingLandFromAgriculturalProductionPortalModule } from './modules/excluding-land-from-agricultural-production-portal/excluding-land-from-agricultural-production-portal.module';
import { FreeServicesPortalModule } from './modules/free-services-portal/free-services-portal.module';
import { GeodeticAndCartographicServiceDataRequestModule } from './modules/geodetic-and-cartographic-service-data-request/geodetic-and-cartographic-service-data-request.module';
import { GRPNewGrantRequestFormService } from './modules/granting-rights-portal/services/new-grant-request-form/new-grant-request-form.service';
import { HouseholdSewageTreatmentNotificationModule } from './modules/household-sewage-treatment-notification/household-sewage-treatment-notification.module';
import { InstallationNotificationWithoutEmissionPermitModule } from './modules/installation-notification-without-emission-permit/installation-notification-without-emission-permit.module';
import { LandAndBuildingRegisterDataChangesNotificationModule } from './modules/land-and-building-register-data-changes-notification/land-and-building-register-data-changes-notification.module';
import { LandAndBuildingRegisterDataUpdateRequestModule } from './modules/land-and-building-register-data-update-request/land-and-building-register-data-update-request.module';
import { LandAndBuildingRegisterDataUpdateUponEntitiesRequestModule } from './modules/land-and-building-register-data-update-upon-entities-request/land-and-building-register-data-update-upon-entities-request.module';
import { LaneOccupancyPortalModule } from './modules/lane-occupancy-portal/lane-occupancy-portal.module';
import { MonumentConservationGrantRequestModule } from './modules/monument-conservation-grant-request/monument-conservation-grant-request.module';
import { MonumentInstallationRequestModule } from './modules/monument-installation-request/monument-installation-request.module';
import { NotObjectedNotificationRightsObligationsTransferModule } from './modules/not-objected-notification-rights-obligations-transfer/not-objected-notification-rights-obligations-transfer.module';
import { OfficeDepartmentsPortalModule } from './modules/office-departments-portal/office-departments-portal.module';
import { OwnershipConversionFeeCalculationRequestModule } from './modules/ownership-conversion-fee-calculation-request/ownership-conversion-fee-calculation-request.module';
import { OwnershipConversionPortalModule } from './modules/ownership-conversion-portal/ownership-conversion-portal.module';
import { PermanentManagementFeeDiscountDecisionGrantingRequestModule } from './modules/permanent-management-fee-discount-decision-granting-request/permanent-management-fee-discount-decision-granting-request.module';
import { PlanningActRequestModule } from './modules/planning-department/planning-act-request/planning-act-request.module';
import { PlanningDepartmentModule } from './modules/planning-department/planning-department.module';
import { PlotLandDevArchConstrProjSeparateDecisionModule } from './modules/plot-land-dev-arch-constr-proj-separate-decision/plot-land-dev-arch-constr-proj-separate-decision.module';
import { PokPortalModule } from './modules/pok-portal/pok-portal.module';
import { PCLBRPModernizationDetailsService } from './modules/presentation-comments-land-building-registry-project/services/modernization-details/modernization-details.service';
import { PropertyLeasePortalModule } from './modules/property-lease-portal/property-lease-portal.module';
import { PropertyOccupancyTechnicalInstallationConsentModule } from './modules/property-occupancy-technical-installation-consent/property-occupancy-technical-installation-consent.module';
import { RoadExitPortalModule } from './modules/road-exit-portal/road-exit-portal.module';
import { SingleFamilyBuildingConstructionIntentionTypeDictionaryService } from './modules/single-family-building-70-construction-conversion/services/sfb-construction-intention-type-dictionary/sfb-construction-intention-type-dictionary.service';
import { SingleFamilyBuilding70ConstructionConversionModule } from './modules/single-family-building-70-construction-conversion/single-family-building-70-construction-conversion.module';
import { SingleFamilyBuildingConstructionConversionModule } from './modules/single-family-building-construction-conversion/single-family-building-construction-conversion.module';
import { SoilClassificationOfLandModule } from './modules/soil-classification-of-land/soil-classification-of-land.module';
import { SpatialInformationPortalModule } from './modules/spatial-information-portal/spatial-information-portal.module';
import { StreetNamingPortalModule } from './modules/street-naming-portal/street-naming-portal.module';
import { SupplementaryParcelPurchasePortalModule } from './modules/supplementary-parcel-purchase-portal/supplementary-parcel-purchase-portal.module';
import { DownloadableApplicationsService } from './modules/surveyor-portal/downloadable-application/downloadable-applications.service';
import { TemporaryStructureBuildingPermitModule } from './modules/temporary-structure-building-permit/temporary-structure-building-permit.module';
import { TreesAndShrubsFallNotificationModule } from './modules/trees-and-shrubs-fall-notification/trees-and-shrubs-fall-notification.module';
import { TreesAndShrubsFellingWithDecisionRequestModule } from './modules/trees-and-shrubs-felling-with-decision-request/trees-and-shrubs-felling-with-decision-request.module';
import { TreesAndShrubsRemovalNotificationModule } from './modules/trees-and-shrubs-removal-notification/trees-and-shrubs-removal-notification.module';
import { AddressService } from './services/address/address.service';
import { AdministrativeDivisionService } from './services/administrative-division/administrative-division.service';
import { AppLocalStorageService } from './services/app-local-storage/app-local-storage.service';
import { AppModeProviderService } from './services/app-mode-provider/app-mode-provider.service';
import { AppTitleService } from './services/app-title/app-title.service';
import { AuthorizedPersonFormService } from './services/authorized-person-form/authorized-person-form.service';
import { AuthorizedPersonToRegistryViewFormService } from './services/authorized-person-to-registry-view-form/authorized-person-to-registry-view-form.service';
import { CautionsService } from './services/cautions/cautions.service';
import { ClosedAreasService } from './services/closed-areas/closed-areas.service';
import { CommunityService } from './services/community/community.service';
import { ConfigManagerService } from './services/config-manager/config-manager.service';
import { ConnectionAndNetworkService } from './services/connection-and-network/connection-and-network.service';
import { DesignerSettingsService } from './services/designer-settings/designer-settings.service';
import { DistrictSelectionService } from './services/district-selection/district-selection.service';
import { DocTypeService } from './services/doc-type/doc-type.service';
import { DocumentIdVerificationService } from './services/document-id-verification/document-id-verification.service';
import { DocumentSubjectService } from './services/document-subject/document-subject.service';
import { DocumentTypeService } from './services/document-type/document-type.service';
import { DomRefService } from './services/dom-ref/dom-ref.service';
import { EcoDocumentSearchFormService } from './services/eco-document-search-form/eco-document-search-form.service';
import { EcoDocumentSearchService } from './services/eco-document-search/eco-document-search.service';
import { EcoMapSettingsService } from './services/eco-map-settings/eco-map-settings.service';
import { EcoNewsletterFormService } from './services/eco-newsletter-form/eco-newsletter-form.service';
import { EcoNewsletterService } from './services/eco-newsletter/eco-newsletter.service';
import { FileTypeMatcherService } from './services/file-type-matcher/file-type-matcher.service';
import { FormsStateService } from './services/forms-state/forms-state.service';
import { HttpInterceptorService } from './services/http-interceptor/http-interceptor.service';
import { InvestorDetailsFormService } from './services/investor-details-form/investor-details-form.service';
import { NewDesignerRequestService } from './services/new-designer-request/new-designer-request.service';
import { NewRequestHelperService } from './services/new-request-helper/new-request-helper.service';
import { OfficeDepartmentsService } from './services/office-departments/office-departments.service';
import { ParcelService } from './services/parcel/parcel.service';
import { PlaceService } from './services/place/place.service';
import { PostalCodeService } from './services/postal-code/postal-code.service';
import { PublicRegisterService } from './services/public-register/public-register.service';
import { RequestWorkspaceStateService } from './services/request-workspace-state/request-workspace-state.service';
import { SessionRefreshTriggerService } from './services/session-refresh-trigger/session-refresh-trigger.service';
import { SessionService } from './services/session/session.service';
import { SettingsService } from './services/settings/settings.service';
import { SharingPurposeService } from './services/sharing-purpose/sharing-purpose.service';
import { ShippingFormService } from './services/shipping-form/shipping-form.service';
import { SwManagerService } from './services/sw-manager/sw-manager.service';
import { SystemCountyBackgroundService } from './services/system-county-background/system-county-background.service';
import { TimerService } from './services/timer/timer.service';
import { UiVerboseService } from './services/ui-verbose/ui-verbose.service';
import { WebPortalService } from './services/web-portal/web-portal.service';
import { WebRequestService } from './services/web-request/web-request.service';
import { WindowRefService } from './services/window-ref/window-ref.service';
import { windowFactory } from './services/window/window.factory';
import { FeeWaiverFormService } from './shared/fee-waiver/fee-waver-form.service';
import { PermissionFormService } from './shared/permission-number-modal/permission-form/permission-form.service';
import { SharedModule } from './shared/shared.module';

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    AppVersionUpdateComponent,
    NoAccessComponent,
    NavbarProviderComponent,
    AccessibleNavigationComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule,
    NgbModule,
    TranslateModule.forRoot({
      defaultLanguage: 'pl',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      preventDuplicates: true,
    }),
    DesignerPortalModule,
    FreeServicesPortalModule,
    EcoPortalModule,
    RoadExitPortalModule,
    ExcludingLandFromAgriculturalProductionPortalModule,
    EnvironmentalProtectionPortalModule,
    LaneOccupancyPortalModule,
    LandAndBuildingRegisterDataChangesNotificationModule,
    LandAndBuildingRegisterDataUpdateRequestModule,
    LandAndBuildingRegisterDataUpdateUponEntitiesRequestModule,
    SoilClassificationOfLandModule,
    AccessToInformationOnEnvironmentAndItsProtectionModule,
    BuildingPermitModule,
    DemolitionPermitModule,
    BuildingOrPartUseChangeModule,
    TemporaryStructureBuildingPermitModule,
    BuildingPermitModificationDecisionModule,
    NotObjectedNotificationRightsObligationsTransferModule,
    PlotLandDevArchConstrProjSeparateDecisionModule,
    AdjacentPropertyNeedEnterDecisionModule,
    SingleFamilyBuilding70ConstructionConversionModule,
    SingleFamilyBuildingConstructionConversionModule,
    BuildingPermitTransferPortalModule,
    ConstructionPortalModule,
    OfficeDepartmentsPortalModule,
    SpatialInformationPortalModule,
    PlanningDepartmentModule,
    AddressNumberRequestPortalModule,
    PokPortalModule,
    PropertyLeasePortalModule,
    SupplementaryParcelPurchasePortalModule,
    OwnershipConversionPortalModule,
    OwnershipConversionFeeCalculationRequestModule,
    StreetNamingPortalModule,
    CommunalPremisePurchasePortalModule,
    GeodeticAndCartographicServiceDataRequestModule,
    PermanentManagementFeeDiscountDecisionGrantingRequestModule,
    PlanningActRequestModule,
    PropertyOccupancyTechnicalInstallationConsentModule,
    TreesAndShrubsFallNotificationModule,
    TreesAndShrubsFellingWithDecisionRequestModule,
    TreesAndShrubsRemovalNotificationModule,
    InstallationNotificationWithoutEmissionPermitModule,
    MonumentConservationGrantRequestModule,
    MonumentInstallationRequestModule,
    HouseholdSewageTreatmentNotificationModule,
    AppRoutingModule,
    GkModulesModule,
    GkUserSettingsModule,
    FormlyModule.forRoot({
      types: [
        {
          name: 'formly-mat-tree-select-type',
          component: FormlyMatTreeSelectTypeComponent,
          wrappers: ['form-field'],
        },
      ],
    }),
    ServiceWorkerModule.register('./ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseHrefInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ProxyInterceptor,
      multi: true,
    },
    { provide: 'window', useFactory: windowFactory },
    // { provide: NgbDatepickerI18n, useClass: DateNamesTranslationService },
    SwManagerService,
    DomRefService,
    SettingsService,
    SharingPurposeService,
    CommunityService,
    SessionService,
    ParcelService,
    ConnectionAndNetworkService,
    PlaceService,
    PostalCodeService,
    AddressService,
    NewDesignerRequestService,
    FormsStateService,
    ConfigManagerService,
    AppLocalStorageService,
    AppTitleService,
    TimerService,
    RequestWorkspaceStateService,
    DistrictSelectionService,
    SessionRefreshTriggerService,
    DocTypeService,
    FileTypeMatcherService,
    ClosedAreasService,
    UiVerboseService,
    MapSettingsService,
    MapStateService,
    NewRequestHelperService,
    EcoDocumentSearchFormService,
    DocumentTypeService,
    DocumentSubjectService,
    EcoDocumentSearchService,
    AdministrativeDivisionService,
    EcoMapSettingsService,
    EcoNewsletterFormService,
    EcoNewsletterService,
    InvestorDetailsFormService,
    ExclusionPurposeService,
    ShippingFormService,
    PortalConfigAccessGuard,
    SignInConfigAccessGuard,
    PerunSessionGuard,
    WebPortalService,
    WebRequestService,
    SystemConfigBlobGuestService,
    SystemConfigGuestService,
    OfficeDepartmentsService,
    PermissionFormService,
    AuthorizedPersonFormService,
    FeeWaiverFormService,
    ShippingService,
    DesignerSettingsService,
    DocumentIdVerificationService,
    SystemCitiesDataService,
    ColorsService,
    AuthorizedPersonToRegistryViewFormService,
    SystemCountyBackgroundService,
    PublicRegisterService,
    WindowRefService,
    DownloadableApplicationsService,
    AppStorageService,
    PlannedApplicationMessageService,
    CautionsService,
    AppModeProviderService,
    PortalsPermissionsService,
    DataService,
    LoginService,
    DefaultConfigGuestService,
    DefaultConfigService,
    DemonstrationOfApplicantRightService,
    RecaptchaService,
    PzService,
    FormAlertService,
    GRPNewGrantRequestFormService,
    PCLBRPModernizationDetailsService,
    provideAppInitializer(() => {
      const initializerFn = (
        (recaptchaService: RecaptchaService) => (): Promise<string> =>
          firstValueFrom(recaptchaService.getSiteKey())
      )(inject(RecaptchaService));
      return initializerFn();
    }),
    ConstructionIntentionTypeDictionaryService,
    SingleFamilyBuildingConstructionIntentionTypeDictionaryService,
    DocumentSectionService,
    RequestModeService,
    CaseSelectService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
